import type { ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon, type IconName } from '../Icon';
import { IconButton } from '../IconButton';

import styles from './TopBar.module.css';

export interface TopBarLink {
  id: string;
  label: string;
  href?: string;
}

export interface TopBarProps {
  /** The name of the product, beside the logo. */
  brand: string;
  /** Where the logo leads (the product's front page). Without it the logo is no link. */
  brandHref?: string;
  /** What the logo's link is called for assistive technology: "EasyMusic, the front page". */
  brandLabel?: string;
  /** Sections, most important first: a tablet shows the first four, a phone none. */
  links: readonly TopBarLink[];
  activeId: string;
  onNavigate?: (id: string) => void;
  /** Name of the navigation landmark. */
  navLabel?: string;
  /**
   * The section the page is in, for a bar without links of its own: where the
   * navigation is beside the page (SideNav), a phone still reads the title here.
   */
  title?: string;
  /** A small label after the brand, such as a version. */
  badge?: ReactNode;
  /** The reference pitch, shown on desktop: "A4 = 440 Hz". */
  pitch?: string;
  /** Controls of the application's own, before the avatar, on every device: keep them few. */
  actions?: ReactNode;
  /** Whether there is an account to show: false for a visitor who is not signed in. */
  account?: boolean;
  /** Called by the menu button, which tablets and phones show: for what the bar has no room for. */
  onMenu?: () => void;
  menuLabel?: string;
  menuIcon?: IconName;
  /** Whether what the menu button opens is open, for assistive technology. */
  menuOpen?: boolean;
  /** Whether what the avatar opens is open; leave undefined if it opens nothing. */
  accountOpen?: boolean;
  /** Called by the avatar. */
  onAccount?: () => void;
  accountLabel?: string;
  className?: string;
}

/**
 * The header of the application. What it shows follows the device: the whole
 * navigation on desktop, four sections on a tablet, and on a phone only the title of
 * the current section, with the sections themselves in the BottomTabBar.
 */
export function TopBar({
  brand,
  brandHref,
  brandLabel,
  links,
  activeId,
  onNavigate,
  navLabel = 'Main',
  title,
  badge,
  pitch,
  actions,
  account = true,
  onMenu,
  menuLabel = 'Settings',
  menuIcon = 'tune',
  menuOpen,
  accountOpen,
  onAccount,
  accountLabel = 'Account',
  className,
}: TopBarProps) {
  const current = links.find((link) => link.id === activeId);
  // The logo, and on a phone the section that stands in for it, lead to the front page.
  const Brand = brandHref ? 'a' : 'div';
  const link = brandHref ? { href: brandHref, 'aria-label': brandLabel, title: brandLabel } : {};
  return (
    <header className={cx(styles.bar, className)}>
      <Brand className={styles.brand} {...link}>
        <Icon name="logo" size={24} className={styles.logo} />
        <span className={styles.name}>{brand}</span>
        {badge}
      </Brand>
      <Brand className={styles.section} {...link}>
        <span className={styles.tile}>
          <Icon name="logo" size={18} />
        </span>
        <span className={styles.sectionText}>
          <span className={styles.sectionTitle}>{current?.label ?? title ?? brand}</span>
          <span className={styles.sectionBrand}>{brand}</span>
        </span>
      </Brand>

      {links.length > 0 ? (
      <nav aria-label={navLabel} className={styles.nav}>
        {links.map((link, index) => (
          <a
            key={link.id}
            href={link.href ?? `#${link.id}`}
            aria-current={link.id === activeId ? 'page' : undefined}
            className={cx(styles.link, index >= 4 && styles.more)}
            onClick={(event) => {
              if (!onNavigate) return;
              event.preventDefault();
              onNavigate(link.id);
            }}
          >
            {link.label}
          </a>
        ))}
      </nav>
      ) : null}

      <div className={styles.actions}>
        {pitch ? (
          <span className={styles.pitch}>
            <span className={styles.dot} aria-hidden />
            {pitch}
          </span>
        ) : null}
        {actions ? <div className={styles.extra}>{actions}</div> : null}
        {onMenu ? (
          <IconButton icon={menuIcon} variant="ghost" label={menuLabel} className={styles.menu} aria-expanded={menuOpen} onClick={onMenu} />
        ) : null}
        {account ? (
          <button type="button" className={styles.avatar} aria-label={accountLabel} title={accountLabel} aria-expanded={accountOpen} onClick={onAccount}>
            <Icon name="user" size={16} />
          </button>
        ) : null}
      </div>
    </header>
  );
}
